import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  BarChart3,
} from "lucide-react";

// Type definitions
interface Issue {
  type?: string;
  description?: string;
  confidence?: number;
  severity?: "high" | "medium" | "low";
}

interface SeverityCounts {
  high: number;
  medium: number;
  low: number;
}

interface GraphicsFrame {
  timestamp: string;
  time_seconds: number;
  issues: Issue[];
  severity_counts: SeverityCounts;
}

interface GraphicsAnalysisProps {
  data: GraphicsFrame[];
}

type TimeRangeFilter = "all" | "first30" | "first60" | "issues";
type SeverityLevel = "high" | "medium" | "low";

const GraphicsAnalysis: React.FC<GraphicsAnalysisProps> = ({ data }) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [selectedTimeRange, setSelectedTimeRange] =
    useState<TimeRangeFilter>("all");

  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Graphics Analysis
        </h3>
        <p className="text-gray-500">No graphics analysis data available.</p>
      </div>
    );
  }

  // Calculate summary statistics
  const totalIssues: number = data.reduce(
    (sum: number, entry: GraphicsFrame) => sum + (entry.issues?.length || 0),
    0
  );

  const severityTotals: SeverityCounts = data.reduce(
    (totals: SeverityCounts, entry: GraphicsFrame) => ({
      high: totals.high + (entry.severity_counts?.high || 0),
      medium: totals.medium + (entry.severity_counts?.medium || 0),
      low: totals.low + (entry.severity_counts?.low || 0),
    }),
    { high: 0, medium: 0, low: 0 }
  );

  // Filter data based on selected time range
  const filteredData: GraphicsFrame[] =
    selectedTimeRange === "all"
      ? data
      : data.filter((entry: GraphicsFrame) => {
          const timeInSeconds: number = entry.time_seconds || 0;
          switch (selectedTimeRange) {
            case "first30":
              return timeInSeconds <= 30;
            case "first60":
              return timeInSeconds <= 60;
            case "issues":
              return entry.issues && entry.issues.length > 0;
            default:
              return true;
          }
        });

  const getSeverityColor = (severity: SeverityLevel | undefined): string => {
    switch (severity) {
      case "high":
        return "text-red-600 bg-red-50 border-red-200";
      case "medium":
        return "text-yellow-600 bg-yellow-50 border-yellow-200";
      case "low":
        return "text-blue-600 bg-blue-50 border-blue-200";
      default:
        return "text-gray-600 bg-gray-50 border-gray-200";
    }
  };

  const getSeverityIcon = (
    severity: SeverityLevel | undefined
  ): JSX.Element => {
    switch (severity) {
      case "high":
        return <XCircle className="w-4 h-4" />;
      case "medium":
        return <AlertTriangle className="w-4 h-4" />;
      case "low":
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <CheckCircle className="w-4 h-4" />;
    }
  };

  const handleTimeRangeChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    setSelectedTimeRange(event.target.value as TimeRangeFilter);
  };

  const toggleExpanded = (): void => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <BarChart3 className="w-5 h-5" />
          Graphics Analysis ({data.length} frames)
        </h3>
        <button
          onClick={toggleExpanded}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 transition-colors"
          type="button"
          aria-expanded={isExpanded}
          aria-label={
            isExpanded
              ? "Collapse graphics analysis"
              : "Expand graphics analysis"
          }
        >
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
          {isExpanded ? "Collapse" : "Expand"}
        </button>
      </div>

      {/* Summary Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-blue-900">{data.length}</div>
          <div className="text-sm text-blue-700">Total Frames</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-red-900">
            {severityTotals.high}
          </div>
          <div className="text-sm text-red-700">High Issues</div>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-900">
            {severityTotals.medium}
          </div>
          <div className="text-sm text-yellow-700">Medium Issues</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-900">
            {severityTotals.low}
          </div>
          <div className="text-sm text-green-700">Low Issues</div>
        </div>
      </div>

      {/* Filter Options */}
      <div className="flex flex-wrap gap-2 mb-4">
        <select
          value={selectedTimeRange}
          onChange={handleTimeRangeChange}
          className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          aria-label="Filter frames by time range"
        >
          <option value="all">All Frames</option>
          <option value="first30">First 30 seconds</option>
          <option value="first60">First 60 seconds</option>
          <option value="issues">Frames with Issues</option>
        </select>
      </div>

      {/* Detailed Frame Analysis */}
      {isExpanded && (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredData.map((frame: GraphicsFrame, index: number) => (
            <div
              key={`frame-${index}-${frame.timestamp}`}
              className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-900">
                    Frame {index + 1}
                  </span>
                  <span className="text-sm text-gray-600">
                    {frame.timestamp}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({frame.time_seconds?.toFixed(2)}s)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {frame.severity_counts?.high > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                      <XCircle className="w-3 h-3" />
                      {frame.severity_counts.high}
                    </span>
                  )}
                  {frame.severity_counts?.medium > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                      <AlertTriangle className="w-3 h-3" />
                      {frame.severity_counts.medium}
                    </span>
                  )}
                  {frame.severity_counts?.low > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                      <CheckCircle className="w-3 h-3" />
                      {frame.severity_counts.low}
                    </span>
                  )}
                </div>
              </div>

              {frame.issues && frame.issues.length > 0 && (
                <div className="space-y-2">
                  {frame.issues.map((issue: Issue, issueIndex: number) => (
                    <div
                      key={`issue-${index}-${issueIndex}`}
                      className={`flex items-start gap-2 p-2 rounded-md border ${getSeverityColor(
                        issue.severity
                      )}`}
                    >
                      {getSeverityIcon(issue.severity)}
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          {issue.type || "Unknown Issue"}
                        </div>
                        {issue.description && (
                          <div className="text-xs mt-1 opacity-80">
                            {issue.description}
                          </div>
                        )}
                        {typeof issue.confidence === "number" && (
                          <div className="text-xs mt-1 opacity-60">
                            Confidence: {(issue.confidence * 100).toFixed(1)}%
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {(!frame.issues || frame.issues.length === 0) && (
                <div className="text-sm text-gray-500 italic">
                  No issues detected
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {filteredData.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No frames match the selected filter criteria.
        </div>
      )}
    </div>
  );
};

export default GraphicsAnalysis;
